import React from 'react';

import { PlanNode, TrackCode } from '../types';
import {
  CORE_ID,
  DEFAULT_WORLD,
  Edge,
  LayoutMode,
  octiEdgePath,
  orthoEdgePath,
  rgba,
  TRACK_ORIENT,
  World,
  XY,
} from '../helpers';

type Props = {
  edges: Edge[];
  positions: Record<string, XY>;
  byCode: Record<string, PlanNode>;
  trackColor: Record<TrackCode, string>;
  selectedId: string | null;
  hoveredId: string | null;
  focusTrack: TrackCode | null;
  /** Which layout the edges belong to. Optional: defaults to the legacy cross
   *  world + ortho routing, so existing callers (incl. the admin editor) are
   *  unchanged. `octi` switches to the octilinear world + 45°/90° routing. */
  world?: World;
  mode?: LayoutMode;
  /** Per-plan track→axis map (`trackOrient(tracks)`). Within-track edges flow
   *  along their track's axis, so this MUST match the map `computeLayout` used
   *  or edges leave their arm. Defaults to the legacy 4-arm axes. */
  orient?: Record<TrackCode, 'v' | 'h'>;
};

const CORE_COLOR = '#9fb0c8';

const trackOf = (
  code: string,
  byCode: Record<string, PlanNode>
): TrackCode | null =>
  code === CORE_ID ? null : byCode[code]?.track_code ?? null;

/** SVG layer drawing prerequisite (solid) + soft-recommendation (dashed) edges. */
const SkillEdges: React.FC<Props> = ({
  edges,
  positions,
  byCode,
  trackColor,
  selectedId,
  hoveredId,
  focusTrack,
  world = DEFAULT_WORLD,
  mode = 'cross',
  orient: trackAxis = TRACK_ORIENT,
}) => {
  const base: React.ReactNode[] = [];
  const active: React.ReactNode[] = [];

  edges.forEach((edge) => {
    const a = positions[edge.source];
    const b = positions[edge.target];
    if (!a || !b) return;

    const tTrack = trackOf(edge.target, byCode);
    const sTrack = trackOf(edge.source, byCode);
    const color = tTrack ? trackColor[tTrack] : CORE_COLOR;

    const touchesSel =
      !!selectedId &&
      (edge.source === selectedId || edge.target === selectedId);
    const touchesHover =
      !!hoveredId && (edge.source === hoveredId || edge.target === hoveredId);
    const isActive = touchesSel || touchesHover;

    const faded =
      !!focusTrack &&
      sTrack !== focusTrack &&
      tTrack !== focusTrack &&
      !isActive;

    // octi: 45°/90° routing in the octilinear world. cross (default): the
    // original H/V routing — within-track edges flow along the track's axis,
    // cross-track links along whichever axis spans the larger gap.
    let d: string;
    if (mode === 'octi') {
      d = octiEdgePath(a, b, world.center);
    } else {
      const orient: 'v' | 'h' = edge.crossTrack
        ? Math.abs(b.x - a.x) > Math.abs(b.y - a.y)
          ? 'h'
          : 'v'
        : trackAxis[(tTrack || sTrack || 'trader') as TrackCode];
      d = orthoEdgePath(a, b, orient);
    }

    let stroke: string;
    let width: number;
    if (mode === 'octi') {
      // Bold, zoom-independent lines — matches the tuned Shapes sandbox. Applied
      // only in octi mode; cross (v1–v9) keeps its original weights below.
      if (isActive) {
        stroke = color;
        width = edge.soft ? 3 : 6;
      } else if (faded) {
        stroke = rgba(color, 0.06);
        width = 1;
      } else if (edge.soft) {
        stroke = rgba(color, 0.4);
        width = 2;
      } else if (edge.crossTrack) {
        stroke = rgba(color, 0.5);
        width = 2.2;
      } else {
        stroke = rgba(color, 0.7);
        width = 5;
      }
    } else if (isActive) {
      stroke = color;
      width = edge.soft ? 2 : 2.8;
    } else if (faded) {
      stroke = rgba(color, 0.06);
      width = 1.3;
    } else if (edge.soft) {
      stroke = rgba(color, 0.28);
      width = 1.3;
    } else if (edge.crossTrack) {
      stroke = rgba(color, 0.34);
      width = 1.4;
    } else {
      stroke = rgba(color, 0.5);
      width = 1.7;
    }

    // Soft recommendations get their own sparse dash; cross-track prereqs a
    // denser dash; same-track prereqs are solid.
    const dash = edge.soft ? '2 8' : edge.crossTrack ? '6 6' : undefined;

    const node = (
      <path
        key={edge.id}
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={width}
        strokeDasharray={dash}
        strokeLinecap="round"
        // octi lines are screen-px (stay bold when zoomed out); cross unchanged.
        vectorEffect={mode === 'octi' ? 'non-scaling-stroke' : undefined}
      />
    );

    if (isActive) active.push(node);
    else base.push(node);
  });

  return (
    <svg
      className="devplan-edges"
      width={world.size}
      height={world.size}
      viewBox={`0 0 ${world.size} ${world.size}`}
    >
      {base}
      {active}
    </svg>
  );
};

export default SkillEdges;
